import { useEffect, useMemo, useState } from 'react'
import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { Typography, Checkbox, Button } from '@percent/lemonade'
import styles from './SmartApplyVariantA.module.scss'
import { useIntroModalState } from '@percent/cause-dashboard/common/hooks'
import { useSmartApplyTracking } from '@percent/cause-dashboard/common/hooks/useSmartApplyTracking'
import { useEnabledSmartApplyPartners } from '@percent/cause-dashboard/common/hooks/useEnabledSmartApplyPartners/useEnabledSmartApplyPartners'
import {
  ADOBE_SUB_PRODUCT_LOGOS,
  type SmartApplyPartner
} from '@percent/cause-dashboard/constants/smartApplyPartnerConfig'

type Product = {
  name: string
  displayName: string
  description: string
  logo: string
  checked: boolean
}

const toKebabCase = (str: string) => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

const CARD_PARTNERS: SmartApplyPartner[] = ['adobe', 'openai', 'zoom', 'meta', 'claude']

type SmartApplyVariantAProps = {
  hasCanvaApplication: boolean
  setSelectedPartner: (partner: SmartApplyPartner) => void
  appliedPartners?: Set<SmartApplyPartner>
  page?: number | null
  pagesCount?: number
  secondaryAction?: () => void
  onClose: () => void
  registerDismissCallback?: (callback: (() => void) | null) => void
}

export function SmartApplyVariantA({
  hasCanvaApplication,
  setSelectedPartner,
  appliedPartners = new Set(),
  page = null,
  secondaryAction,
  pagesCount,
  onClose,
  registerDismissCallback
}: Readonly<SmartApplyVariantAProps>) {
  const { t } = useTranslation()
  const { recordSmartApplyAction } = useIntroModalState()
  const { enabledPartners, getConfig } = useEnabledSmartApplyPartners()

  const { trackProductApplied, trackDismissed } = useSmartApplyTracking({ variant: 'variant_a', page })

  useEffect(() => {
    registerDismissCallback?.(() => {
      trackDismissed({ appliedProductsCount: appliedPartners.size })
    })
    return () => registerDismissCallback?.(null)
  }, [registerDismissCallback, trackDismissed, appliedPartners.size])

  const isEligibleForAdobeExpress = !hasCanvaApplication

  const enabledSet = useMemo(() => new Set(enabledPartners), [enabledPartners])

  const initialProducts = useMemo<Product[]>(
    () =>
      CARD_PARTNERS.filter(partner => enabledSet.has(partner))
        .map(partner => {
          const config = getConfig(partner)
          return {
            name: partner,
            displayName: partner === 'adobe' ? '' : String(t(config.i18n.card.title as never)),
            description: partner === 'adobe' ? '' : String(t(config.i18n.card.description as never)),
            logo: config.logo,
            checked: appliedPartners.has(partner)
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [t, appliedPartners, enabledSet, getConfig]
  )

  const [products, setProducts] = useState<Product[]>(initialProducts)

  useEffect(() => {
    setProducts(previousProducts => {
      const checkedNames = new Set(previousProducts.filter(product => product.checked).map(product => product.name))

      return initialProducts.map(product => ({
        ...product,
        checked: product.checked || checkedNames.has(product.name)
      }))
    })
  }, [initialProducts])

  const shouldShowPageIndicator = !!(page && pagesCount && pagesCount > 1)

  const getButtonText = () => {
    if (appliedPartners.size === 0) return t('typography.smartApply.notInterested')

    const closeButtonText =
      !page || page === pagesCount ? t('button.close') : t('offersModal.smartApplyPage.submitted.cta')

    if (appliedPartners.size === products.length) return closeButtonText

    return t('typography.smartApply.skipRemaining')
  }

  const handleProductToggle = (productName: string) => {
    setProducts(prev => prev.map(p => (p.name === productName ? { ...p, checked: !p.checked } : p)))
    setSelectedPartner(productName as SmartApplyPartner)
    trackProductApplied({ productName })
  }

  const handleClick = secondaryAction
    ? () => {
        recordSmartApplyAction('dismissed')
        trackDismissed({ appliedProductsCount: appliedPartners.size })
        secondaryAction()
      }
    : () => {
        recordSmartApplyAction(appliedPartners.size > 0 ? 'applied' : 'dismissed')
        trackDismissed({ appliedProductsCount: appliedPartners.size })
        onClose()
      }

  const createCardHandlers = (productName: string, isApplied: boolean) => ({
    onClick: (e: React.MouseEvent) => {
      if (isApplied || (e.target as HTMLElement).closest('input[type="checkbox"]')) return
      handleProductToggle(productName)
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (isApplied || (e.key !== 'Enter' && e.key !== ' ')) return
      e.preventDefault()
      handleProductToggle(productName)
    }
  })

  const renderActionElement = (product: Product) => {
    if (!product.checked) {
      return (
        <Typography variant="default" className={styles.clickToApply}>
          {t('button.apply')}
        </Typography>
      )
    }
    return (
      <Checkbox
        name={product.name}
        variant="default"
        label=""
        onChange={() => handleProductToggle(product.name)}
        active={product.checked}
        dataTestId={`${toKebabCase(product.name)}-checkbox`}
        className={styles.productCheckbox}
      />
    )
  }

  return (
    <div className={styles.smartApplyContainer}>
      <div className={styles.contentWrapper}>
        {shouldShowPageIndicator && (
          <p className={styles.pageIndicator}>
            {page} {t('offersModal.page.of')} {pagesCount}
          </p>
        )}
        <Typography className={styles.title} variant="h2">
          <span role="img" aria-label="sparkles">
            ✨
          </span>{' '}
          {t('typography.smartApply.title')}
        </Typography>

        <div className={styles.subtitles}>
          <Typography variant="large">
            <Box
              dangerouslySetInnerHTML={{
                __html: t('typography.smartApply.subtitle1')
              }}
            />
          </Typography>
        </div>

        <div className={styles.checkboxesContainer}>
          {products.map(product => {
            if (product.name === 'adobe') {
              const isApplied = appliedPartners.has('adobe')
              const handlers = createCardHandlers(product.name, isApplied)
              return (
                <button
                  type="button"
                  key={product.name}
                  className={`${styles.productCard} ${styles.adobeCard} ${isApplied ? styles.appliedCard : ''}`}
                  disabled={isApplied}
                  {...handlers}
                >
                  <div className={styles.adobeProductsContainer}>
                    <div className={styles.adobeProduct}>
                      <img
                        src={ADOBE_SUB_PRODUCT_LOGOS.adobeAcrobat}
                        alt={t('typography.smartApply.adobeAcrobatPro.title')}
                        className={styles.productLogo}
                      />
                      <div className={styles.adobeProductInfo}>
                        <Typography variant="default-bold" className={styles.productTitle}>
                          {t('typography.smartApply.adobeAcrobatPro.title')}
                        </Typography>
                        <Typography $color="gray300" variant="default" className={styles.productDescription}>
                          {t('typography.smartApply.adobeAcrobatPro.description')}
                        </Typography>
                      </div>
                    </div>
                    {isEligibleForAdobeExpress && (
                      <div className={styles.adobeProduct}>
                        <img
                          src={ADOBE_SUB_PRODUCT_LOGOS.adobeExpress}
                          alt={t('typography.smartApply.adobeExpress.title')}
                          className={styles.productLogo}
                        />
                        <div className={styles.adobeProductInfo}>
                          <Typography variant="default-bold" className={styles.productTitle}>
                            {t('typography.smartApply.adobeExpress.title')}
                          </Typography>
                          <Typography $color="gray300" variant="default" className={styles.productDescription}>
                            {t('typography.smartApply.adobeExpress.description')}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                  {renderActionElement(product)}
                </button>
              )
            }

            const isApplied = appliedPartners.has(product.name as SmartApplyPartner)
            const handlers = createCardHandlers(product.name, isApplied)
            return (
              <button
                type="button"
                key={product.name}
                className={`${styles.productCard} ${isApplied ? styles.appliedCard : ''}`}
                disabled={isApplied}
                {...handlers}
              >
                <img src={product.logo} alt={product.displayName} className={styles.productLogo} />
                <div className={styles.productInfo}>
                  <Typography variant="default-bold" className={styles.productTitle}>
                    {product.displayName}
                  </Typography>
                  <Typography $color="gray300" variant="default" className={styles.productDescription}>
                    {product.description}
                  </Typography>
                </div>
                {renderActionElement(product)}
              </button>
            )
          })}
        </div>
      </div>

      <div className={`${styles.buttonWrapper} ${styles.applyButtonWrapper}`}>
        <Button variant="tertiary" stretch onClick={handleClick} data-testid="smart-apply-skip-button">
          {getButtonText()}
        </Button>
      </div>
    </div>
  )
}
